import { motion } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const faqs = [
  {
    question: 'What is ADAS calibration?',
    answer: 'ADAS (Advanced Driver Assistance Systems) calibration is the process of adjusting and configuring vehicle safety systems like cameras, radar, and sensors to ensure they function correctly. This is required after windscreen replacement, collision repairs, suspension work, or wheel alignment changes.',
  },
  {
    question: 'Why is calibration necessary after repairs?',
    answer: 'Modern vehicles rely on precisely positioned sensors and cameras for safety systems like lane departure warning, adaptive cruise control, and automatic emergency braking. Even minor changes to vehicle geometry or component replacement can affect system accuracy, making recalibration essential for safe operation.',
  },
  {
    question: 'What\'s the difference between static and dynamic calibration?',
    answer: 'Static calibration is performed in a controlled workshop environment using specific targets positioned around the vehicle. Dynamic calibration requires driving the vehicle on suitable roads to allow systems to self-calibrate. Some systems require both types of calibration.',
  },
  {
    question: 'How long does calibration take?',
    answer: 'Calibration time varies depending on the vehicle make, model, and systems involved. Typically, a single-system static calibration takes 1-2 hours. Vehicles with multiple ADAS features or requiring dynamic calibration may take longer.',
  },
  {
    question: 'Do you provide documentation for insurers?',
    answer: 'Yes, every calibration includes comprehensive documentation including pre and post-scan reports, OEM procedure references, calibration confirmation data, technician credentials, and photos where required. Our reports are designed to meet insurer audit requirements.',
  },
  {
    question: 'What areas do you service?',
    answer: 'We provide calibration services across South Australia, including Adelaide metropolitan and surrounding regions. For mobile services, please contact us to confirm coverage for your location.',
  },
  {
    question: 'Can you calibrate all vehicle makes and models?',
    answer: 'We calibrate ADAS systems for most vehicle manufacturers including Australian, European, Japanese, and American brands. Contact us with your specific vehicle details and we\'ll confirm coverage and any special requirements.',
  },
  {
    question: 'What equipment do you use?',
    answer: 'We use industry-leading diagnostic and calibration equipment that meets OEM specifications. Our target systems, diagnostic tools, and software are regularly updated to support the latest vehicle models and systems.',
  },
];

export const FAQ = () => {
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-20 md:py-28 bg-white"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <span className="text-blue-600 text-sm font-semibold tracking-widest uppercase mb-4 block">
            FAQ
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-base sm:text-lg">
            Common questions about ADAS calibration services
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="border-b border-slate-200"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-heading text-lg font-semibold text-slate-900 hover:text-blue-600 py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-base leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
};
